import React, { useState, useEffect } from 'react';
import { useAPR } from '../context/APRContext';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';

const Dimensions: React.FC = () => {
  const { dispatch } = useAPR();
  const { hasPermission, currentUser } = useAuth();
  const [editingDimension, setEditingDimension] = useState<any>(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showCreateDimensionForm, setShowCreateDimensionForm] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [dimensionsData, setDimensionsData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    dimensionId: ''
  });

  // File upload state
  const [uploadedDimensions, setUploadedDimensions] = useState<any[]>([]);
  const [showUploadPreview, setShowUploadPreview] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Delete all state
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteStatus, setDeleteStatus] = useState<string | null>(null);

  useEffect(() => {
    loadDimensionsFromDatabase();
  }, []);

  const loadDimensionsFromDatabase = async () => {
    try {
      setLoading(true);
      console.log('🔄 Dimensions: Loading from database...');
      const dimensions = await apiService.getDimensions();
      console.log('📊 Dimensions: Loaded', dimensions.length, 'dimensions from database');
      setDimensionsData(dimensions);
    } catch (error) {
      console.error('Failed to load dimensions:', error);
    } finally {
      setLoading(false);
    }
  };

  const canModify = hasPermission('canManageSettings');
  const isAdmin = currentUser?.role === 'Administrator';

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateDimension = () => {
    setEditingDimension(null);
    setFormData({
      name: '',
      description: '',
      dimensionId: ''
    });
    setShowCreateDimensionForm(true);
  };

  const handleViewDimension = (dimension: any) => {
    setEditingDimension(dimension);
    setFormData({
      name: dimension.name,
      description: dimension.description || '',
      dimensionId: ''
    });
    setViewMode(true);
    setShowEditForm(true);
  };

  const handleEditDimension = (dimension: any) => {
    setEditingDimension(dimension);
    setFormData({
      name: dimension.name,
      description: dimension.description || '',
      dimensionId: ''
    });
    setViewMode(false);
    setShowEditForm(true);
  };

  const handleDeleteDimension = async (dimension: any) => {
    if (!canModify) return;

    const confirmed = window.confirm(`Are you sure you want to delete dimension "${dimension.name}"? This will also delete all its components and attributes.`);
    if (!confirmed) return;

    try {
      await apiService.deleteDimension(dimension.id);
      console.log('✅ Dimension deleted successfully');
      await loadDimensionsFromDatabase();
    } catch (error: any) {
      console.error('Failed to delete dimension:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to delete dimension';
      alert(`Error: ${errorMessage}`);
    }
  };


  const handleCancelEdit = () => {
    setShowEditForm(false);
    setShowCreateDimensionForm(false);
    setEditingDimension(null);
    setViewMode(false);
    setFormData({ name: '', description: '', dimensionId: '' });
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      alert('Dimension name is required');
      return;
    }

    // Check for duplicate name (case-insensitive)
    const trimmedName = formData.name.trim().toLowerCase();
    const duplicateDimension = dimensionsData.find(d =>
      d.name.toLowerCase() === trimmedName &&
      (!editingDimension || d.id !== editingDimension.id)
    );

    if (duplicateDimension) {
      alert(`A dimension with the name "${formData.name.trim()}" already exists. Please use a different name.`);
      return;
    }

    try {
      const dimensionData = {
        name: formData.name,
        description: formData.description
      };

      if (editingDimension) {
        await apiService.updateDimension(editingDimension.id, dimensionData);
        console.log('✅ Dimension updated successfully');
      } else if (showCreateDimensionForm) {
        const result = await apiService.createDimension(dimensionData);
        console.log('✅ Dimension created successfully:', result);
      }

      await loadDimensionsFromDatabase();
      handleCancelEdit();
    } catch (error: any) {
      console.error('Failed to save dimension:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to save dimension';
      alert(`Error: ${errorMessage}`);
    }
  };

  // Dimension bulk upload functions
  const downloadDimensionsTemplate = () => {
    const headers = ['Dimension', 'Description'];

    const csvContent = headers.join(',') + '\n' +
      'Business Value & Strategic Fit,Evaluates alignment with business strategy and global standards\n' +
      'Business Case,Assesses cost-effectiveness and financial impact\n' +
      'Application Performance,Measures application reliability and user satisfaction\n' +
      'Application Risk Mitigation,Evaluates business continuity and risk management\n' +
      'Architectural Maturity,Assesses technical architecture and development quality';

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'dimensions_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const downloadDimensionsData = () => {
    if (dimensionsData.length === 0) {
      alert('No dimensions data to download');
      return;
    }

    const headers = ['Dimension', 'Description'];
    const csvRows = [headers.join(',')];

    dimensionsData.forEach(dim => {
      const row = [
        `"${(dim.name || '').replace(/"/g, '""')}"`,
        `"${(dim.description || '').replace(/"/g, '""')}"`
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dimensions_data_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      parseDimensionsCSV(text);
    };
    reader.readAsText(file);
  };

  const parseDimensionsCSV = (csvText: string) => {
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      alert('CSV file is empty or invalid');
      return;
    }

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const dimensionIndex = headers.findIndex(h => h.includes('dimension'));
    const descriptionIndex = headers.findIndex(h => h.includes('description'));

    if (dimensionIndex === -1) {
      alert('CSV must have a "Dimension" column');
      return;
    }

    const parsedDimensions = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
      if (values[dimensionIndex]) {
        parsedDimensions.push({
          name: values[dimensionIndex],
          description: descriptionIndex !== -1 ? values[descriptionIndex] : '',
          row: i + 1
        });
      }
    }

    setUploadedDimensions(parsedDimensions);
    setShowUploadPreview(true);
  };

  const processBulkDimensionUpload = async () => {
    try {
      const dimensionsToCreate = uploadedDimensions.filter(dim => {
        const existing = dimensionsData.find(d => d.name.toLowerCase() === dim.name.toLowerCase());
        return !existing;
      });

      const skipped = uploadedDimensions.filter(dim => {
        const existing = dimensionsData.find(d => d.name.toLowerCase() === dim.name.toLowerCase());
        return existing;
      });

      if (skipped.length > 0) {
        alert(`Skipping ${skipped.length} duplicate dimension(s): ${skipped.map(d => d.name).join(', ')}`);
      }

      if (dimensionsToCreate.length === 0) {
        alert('No new dimensions to create.');
        setShowUploadPreview(false);
        setUploadedDimensions([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      const createdDimensions = [];
      for (const dimData of dimensionsToCreate) {
        try {
          const result = await apiService.createDimension({
            name: dimData.name,
            description: dimData.description
          });
          createdDimensions.push(result);
        } catch (error) {
          console.error(`Failed to create dimension: ${dimData.name}`, error);
        }
      }

      alert(`Successfully created ${createdDimensions.length} dimension(s)`);
      await loadDimensionsFromDatabase();
      setShowUploadPreview(false);
      setUploadedDimensions([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error creating dimensions:', error);
      alert(`Error creating dimensions: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const cancelBulkUpload = () => {
    setShowUploadPreview(false);
    setUploadedDimensions([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeleteAllData = async () => {
    if (!isAdmin) {
      setDeleteStatus('Only administrators can delete all dimensions');
      return;
    }

    if (deleteConfirmText !== 'DELETE') {
      alert('Please type "DELETE" to confirm deletion');
      return;
    }

    setLoading(true);
    setDeleteStatus('Deleting all dimensions...');

    try {
      const dimensions = await apiService.getDimensions();
      let deletedCount = 0;

      for (const dim of dimensions) {
        try {
          await apiService.deleteDimension(dim.id);
          deletedCount++;
        } catch (error) {
          console.error(`Failed to delete dimension ${dim.id}:`, error);
        }
      }

      setDeleteStatus(`Successfully deleted ${deletedCount} dimensions`);
      await loadDimensionsFromDatabase();
      setShowDeleteAllModal(false);
      setDeleteConfirmText('');
    } catch (error) {
      console.error('Error deleting all dimensions:', error);
      setDeleteStatus('Failed to delete all dimensions. Check console for details.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDeleteAll = () => {
    setShowDeleteAllModal(false);
    setDeleteConfirmText('');
    setDeleteStatus(null);
  };

  const getDimensionIcon = (dimensionName: string) => {
    const icons: Record<string, string> = {
      'Business Value and Strategic Fit': '🎯',
      'Business Case': '💰',
      'Application Performance': '⚡',
      'Application Risk Mitigation': '🛡️',
      'Architectural Maturity': '🏗️'
    };
    return icons[dimensionName] || '📋';
  };

  const getComponentIcon = (componentName: string) => {
    const icons: Record<string, string> = {
      'Alignment to Global Standards': '🌐',
      'Alignment to Strategy': '🎯',
      'Importance to Business': '⭐',
      'Usage Pattern': '📈',
      'Infrastructure Costs': '💻',
      'Operational Costs': '🔧',
      'Migration Costs': '🔄',
      'Process Improvement Costs': '📊',
      'User Perception and Experience': '👥',
      'Application Value': '💎',
      'Operational Performance': '⚙️',
      'Functional / Process Coverage': '📋',
      'Business Criticality': '🚨',
      'Risk of Failure': '⚠️',
      'Technological Risk': '🔍',
      'Risk Mitigation': '🛡️',
      'Quality of Support': '🤝',
      'Architecture and Design': '🏛️',
      'Platform Maturity': '📱',
      'Development Quality': '✅',
      'Agility & Scalability': '🚀',
      'Security Standards Alignment': '🔐'
    };
    return icons[componentName] || '📄';
  };

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">Dimensions Management</h1>
          </div>
          <div className="page-header__actions">
            {canModify && (
              <>
                <button
                  className="btn btn-primary"
                  onClick={handleCreateDimension}
                  title="Add a new dimension"
                >
                  ➕ Add Dimension
                </button>
                <button
                  className="btn btn-outline-info"
                  onClick={downloadDimensionsTemplate}
                  title="Download CSV template for bulk dimension upload"
                >
                  📋 Download Template
                </button>
                <button
                  className="btn btn-outline-primary"
                  onClick={() => fileInputRef.current?.click()}
                  title="Upload CSV file with dimensions"
                >
                  📤 Upload Dimensions
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept=".csv"
                  style={{ display: 'none' }}
                />
              </>
            )}
            {isAdmin && dimensionsData.length > 0 && (
              <button
                className="btn btn-danger"
                onClick={() => setShowDeleteAllModal(true)}
                disabled={loading}
              >
                🗑️ Delete All Data
              </button>
            )}
            {dimensionsData.length > 0 && (
              <button
                className="btn btn-outline-success"
                onClick={downloadDimensionsData}
                title="Download current dimensions data as CSV"
                style={{ border: '2px solid #28a745' }}
              >
                📥 Download Data
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="master-data-container">

      {/* Upload Preview Modal */}
      {showUploadPreview && uploadedDimensions.length > 0 && (
        <div className="modal-overlay" onClick={cancelBulkUpload}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              minWidth: '600px',
              maxWidth: '800px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 8px 24px rgba(0,0,0,0.15)',
              position: 'relative'
            }}
          >
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{
                margin: '0 0 8px 0',
                fontSize: '24px',
                fontWeight: '500',
                color: '#1a1a1a'
              }}>
                Preview Dimension Upload
              </h2>
              <p style={{
                margin: 0,
                fontSize: '14px',
                color: '#666'
              }}>
                Review {uploadedDimensions.length} dimension{uploadedDimensions.length !== 1 ? 's' : ''} to be imported
              </p>
            </div>

            <div style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '24px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                  <tr>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid #dee2e6', fontSize: '14px' }}>Dimension</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid #dee2e6', fontSize: '14px' }}>Description</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', borderBottom: '2px solid #dee2e6', fontSize: '14px', minWidth: '100px' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {uploadedDimensions.map((dim, index) => {
                    const existing = dimensionsData.find(d => d.name.toLowerCase() === dim.name.toLowerCase());
                    const status = existing ? 'Duplicate' : 'Valid';
                    return (
                      <tr key={index} style={{ borderBottom: '1px solid #dee2e6' }}>
                        <td style={{ padding: '12px', fontSize: '14px' }}>{dim.name}</td>
                        <td style={{ padding: '12px', fontSize: '14px', maxWidth: '300px' }}>{dim.description}</td>
                        <td style={{ padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: '500' }}>
                          <span style={{
                            color: status === 'Valid' ? '#28a745' : '#856404',
                            backgroundColor: status === 'Valid' ? '#d4edda' : '#fff3cd',
                            padding: '4px 12px',
                            borderRadius: '4px',
                            display: 'inline-block'
                          }}>
                            {status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              marginTop: '24px'
            }}>
              <button
                type="button"
                onClick={cancelBulkUpload}
                style={{
                  padding: '10px 24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#666',
                  backgroundColor: 'transparent',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={processBulkDimensionUpload}
                style={{
                  padding: '10px 24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'white',
                  backgroundColor: '#1976d2',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#1565c0'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#1976d2'}
              >
                Confirm Upload
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Form Modal */}
      {(showEditForm || showCreateDimensionForm) && (
        <div className="modal-overlay" onClick={handleCancelEdit}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              minWidth: '500px',
              maxWidth: '600px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 8px 24px rgba(0,0,0,0.15)',
              position: 'relative'
            }}
          >
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{
                margin: '0 0 8px 0',
                fontSize: '24px',
                fontWeight: '500',
                color: '#1a1a1a'
              }}>
                {viewMode
                  ? 'View Dimension'
                  : (showCreateDimensionForm
                    ? 'Create New Dimension'
                    : editingDimension
                      ? 'Edit Dimension'
                      : 'Edit Component')
                }
              </h2>
              <p style={{
                margin: 0,
                fontSize: '14px',
                color: '#666'
              }}>
                {viewMode
                  ? 'Dimension details (read-only)'
                  : (showCreateDimensionForm
                    ? 'Add a new dimension to the APR framework'
                    : 'Update dimension information')
                }
              </p>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  {showCreateDimensionForm || editingDimension ? 'Dimension' : 'Component'} Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  disabled={viewMode}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '14px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    boxSizing: 'border-box',
                    backgroundColor: viewMode ? '#f5f5f5' : 'white',
                    cursor: viewMode ? 'not-allowed' : 'text'
                  }}
                  placeholder="Enter name"
                />
              </div>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  {showCreateDimensionForm || editingDimension ? 'Dimension' : 'Component'} Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                  disabled={viewMode}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '14px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    backgroundColor: viewMode ? '#f5f5f5' : 'white',
                    cursor: viewMode ? 'not-allowed' : 'text'
                  }}
                  placeholder="Describe what this evaluates and its purpose in the assessment framework"
                />
                <small style={{
                  display: 'block',
                  marginTop: '6px',
                  fontSize: '12px',
                  color: '#666'
                }}>
                  This description helps users understand what aspects this covers during application assessment.
                </small>
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                marginTop: '24px'
              }}>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#666',
                    backgroundColor: 'transparent',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  {viewMode ? 'Close' : 'Cancel'}
                </button>
                {!viewMode && (
                  <button
                    type="submit"
                    style={{
                      padding: '10px 24px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: 'white',
                      backgroundColor: '#1976d2',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#1565c0'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#1976d2'}
                  >
                    {showCreateDimensionForm
                      ? 'Create Dimension'
                      : editingDimension
                        ? 'Save Dimension'
                        : 'Save Component'
                    }
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Material Design Table */}
      <div className="md-table-container">
        <table className="md-table" style={{ fontSize: '14px', tableLayout: 'fixed', width: '100%' }}>
          <colgroup>
            <col style={{ width: '20%' }} />
            <col style={{ width: '45%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '13%' }} />
            {canModify && <col style={{ width: '10%' }} />}
          </colgroup>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '12px', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>Dimension</th>
              <th style={{ textAlign: 'left', padding: '12px', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>Description</th>
              <th style={{ textAlign: 'left', padding: '12px', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>Created By</th>
              <th style={{ textAlign: 'left', padding: '12px', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>Created Date</th>
              {canModify && <th style={{ textAlign: 'center', padding: '12px', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {dimensionsData.map((dimension, index) => (
              <tr key={dimension.id}>
                <td style={{ textAlign: 'left', padding: '12px', fontSize: '14px', wordWrap: 'break-word' }}>
                  {dimension.name}
                </td>
                <td style={{ textAlign: 'left', padding: '12px', fontSize: '14px', wordWrap: 'break-word', whiteSpace: 'normal' }}>
                  {dimension.description || 'No description available'}
                </td>
                <td style={{ textAlign: 'left', padding: '12px', fontSize: '14px' }}>
                  {dimension.created_by || dimension.createdBy || 'System'}
                </td>
                <td style={{ textAlign: 'left', padding: '12px', fontSize: '14px' }}>
                  {(() => {
                    const dateStr = dimension.created_date || dimension.createdDate || dimension.created_at;
                    if (!dateStr) return 'N/A';
                    const date = new Date(dateStr);
                    return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    });
                  })()}
                </td>
                {canModify && (
                  <td style={{ textAlign: 'center', padding: '12px', fontSize: '14px' }}>
                    <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                      <button
                        className="action-btn view-btn"
                        onClick={() => handleViewDimension(dimension)}
                        title="View dimension details"
                        style={{
                          padding: '4px 6px',
                          backgroundColor: 'transparent',
                          color: '#1976d2',
                          border: '1px solid #1976d2',
                          borderRadius: '4px',
                          fontSize: '14px',
                          cursor: 'pointer',
                          minWidth: 'auto'
                        }}
                      >
                        👁️
                      </button>
                      <button
                        className="action-btn edit-btn"
                        onClick={() => handleEditDimension(dimension)}
                        title="Edit dimension"
                        style={{
                          padding: '4px 6px',
                          backgroundColor: 'transparent',
                          color: '#1976d2',
                          border: '1px solid #1976d2',
                          borderRadius: '4px',
                          fontSize: '14px',
                          cursor: 'pointer',
                          minWidth: 'auto'
                        }}
                      >
                        ✏️
                      </button>
                      <button
                        className="action-btn delete-btn"
                        onClick={() => handleDeleteDimension(dimension)}
                        title="Delete dimension"
                        style={{
                          padding: '4px 6px',
                          backgroundColor: 'transparent',
                          color: '#d32f2f',
                          border: '1px solid #d32f2f',
                          borderRadius: '4px',
                          fontSize: '14px',
                          cursor: 'pointer',
                          minWidth: 'auto'
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {loading && (
        <div className="empty-state">
          <div className="empty-state-icon">⏳</div>
          <h3>Loading Dimensions</h3>
          <p>Please wait while we fetch framework dimensions...</p>
        </div>
      )}

      {(!dimensionsData || dimensionsData.length === 0) && !loading && (
        <div className="empty-state">
          <div className="empty-state-icon">📊</div>
          <h3>No Dimensions Available</h3>
          <p>No framework dimensions have been configured yet. Create your first dimension to start building the APR assessment framework.</p>
          {canModify && (
            <div className="empty-state-actions">
              <button
                className="btn btn-primary"
                onClick={handleCreateDimension}
              >
                Create Dimension
              </button>
            </div>
          )}
        </div>
      )}

      {/* Delete All Confirmation Modal */}
      {showDeleteAllModal && (
        <div className="form-modal">
          <div className="form-modal-content" style={{ maxWidth: '500px' }}>
            <div className="form-modal-header" style={{ backgroundColor: '#dc3545' }}>
              <h2>⚠️ Confirm Delete All Dimensions</h2>
              <button
                className="btn-close"
                onClick={handleCancelDeleteAll}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div style={{ padding: '24px' }}>
              <div style={{
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '6px',
                padding: '16px',
                marginBottom: '20px'
              }}>
                <p style={{ margin: 0, color: '#856404', fontWeight: '600', fontSize: '14px' }}>
                  ⚠️ WARNING: This action cannot be undone!
                </p>
                <p style={{ margin: '8px 0 0 0', color: '#856404', fontSize: '13px' }}>
                  This will permanently delete all {dimensionsData.length} dimensions and their associated components and attributes from the system.
                </p>
              </div>

              <div className="form-group">
                <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                  Type <strong style={{ color: '#dc3545' }}>DELETE</strong> to confirm:
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Type DELETE here"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #dc3545',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontFamily: 'monospace'
                  }}
                  autoFocus
                />
              </div>

              <div className="form-actions" style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  className="btn btn-secondary"
                  onClick={handleCancelDeleteAll}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handleDeleteAllData}
                  disabled={loading || deleteConfirmText !== 'DELETE'}
                >
                  {loading ? 'Deleting...' : 'Delete All Dimensions'}
                </button>
              </div>

              {deleteStatus && (
                <div style={{
                  marginTop: '16px',
                  padding: '12px',
                  borderRadius: '4px',
                  backgroundColor: deleteStatus.includes('Failed') || deleteStatus.includes('error') ? '#f8d7da' : '#d4edda',
                  color: deleteStatus.includes('Failed') || deleteStatus.includes('error') ? '#721c24' : '#155724',
                  fontSize: '14px'
                }}>
                  {deleteStatus}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Dimensions;